import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getInstallations } from "../../services/fireSafetyLogbookApi";
import { useUser } from "../../context/UserContext";
import {
  getCustomerPortalInstallations,
  toPortalErrorMessage,
} from "../../services/customerPortalApi";

const VIEW_STORAGE_KEY = "portal.installations.view";
const DEFAULT_LIMIT = 25;

export default function PortalInstallationsPage() {
  const { user } = useUser();
  const accountType = String(user?.accountType || "internal").trim().toLowerCase();
  const isCustomerPortalUser = accountType === "customer_portal";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [view, setView] = useState(() => loadViewMode());
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = DEFAULT_LIMIT;

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        if (isCustomerPortalUser) {
          const response = await getCustomerPortalInstallations({
            page,
            limit,
            q: String(query || "").trim() || undefined,
          });
          if (!active) return;
          setItems(Array.isArray(response?.items) ? response.items : []);
          setTotal(Number(response?.total || 0));
          return;
        }

        const installations = await getInstallations();
        if (!active) return;
        const source = Array.isArray(installations) ? installations : [];
        const q = String(query || "").trim().toLowerCase();
        const filtered = !q
          ? source
          : source.filter((item) => {
            const text = [
              item?.naam,
              item?.name,
              item?.type,
              item?.locatie,
              item?.locationLabel,
              item?.bagAddress?.formattedAddress,
            ]
              .map((value) => String(value || "").toLowerCase())
              .join(" ");
            return text.includes(q);
          });
        const nextTotal = filtered.length;
        const nextTotalPages = Math.max(1, Math.ceil(nextTotal / limit));
        const safePage = Math.min(Math.max(1, page), nextTotalPages);
        const start = (safePage - 1) * limit;
        setItems(filtered.slice(start, start + limit));
        setTotal(nextTotal);
        if (safePage !== page) {
          setPage(safePage);
        }
      } catch (err) {
        if (!active) return;
        setError(
          isCustomerPortalUser
            ? toPortalErrorMessage(err, "Installaties laden mislukt")
            : err?.response?.data?.message || "Installaties laden mislukt"
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [isCustomerPortalUser, limit, page, query]);

  useEffect(() => {
    saveViewMode(view);
  }, [view]);

  const pagination = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(Math.max(0, total) / limit));
    const safePage = Math.min(Math.max(1, page), totalPages);
    return {
      page: safePage,
      totalPages,
      hasPrevious: safePage > 1,
      hasNext: safePage < totalPages,
    };
  }, [limit, page, total]);

  useEffect(() => {
    if (page > pagination.totalPages) {
      setPage(pagination.totalPages);
    }
  }, [page, pagination.totalPages]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <nav className="mb-2 text-xs text-slate-500">
            <Link to="/portaal" className="hover:underline">Dashboard</Link>
            <span className="px-1">/</span>
            <span className="font-semibold text-slate-700">Installaties</span>
          </nav>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Klantportaal</p>
          <h1 className="text-2xl font-bold text-slate-900">Installaties</h1>
          <p className="text-sm text-slate-600">Overzicht van alle installaties en objectlocaties.</p>
        </header>

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
        )}

        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Zoek op naam, type of locatie"
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 sm:w-80"
            />

            <div className="inline-flex rounded-xl border border-slate-300 bg-white p-1">
              <button
                type="button"
                onClick={() => setView("table")}
                className={`rounded-lg px-3 py-1 text-xs font-semibold ${
                  view === "table" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                Tabel
              </button>
              <button
                type="button"
                onClick={() => setView("cards")}
                className={`rounded-lg px-3 py-1 text-xs font-semibold ${
                  view === "cards" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                Kaarten
              </button>
            </div>
          </div>

          <div className="mb-3 flex items-center justify-between gap-2 px-2 text-xs text-slate-600">
            <p>
              Pagina <span className="font-semibold text-slate-800">{pagination.page}</span> van{" "}
              <span className="font-semibold text-slate-800">{pagination.totalPages}</span> • Totaal:{" "}
              <span className="font-semibold text-slate-800">{total}</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={!pagination.hasPrevious || loading}
                className="rounded-lg border border-slate-300 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() =>
                  setPage((current) => Math.min(pagination.totalPages, current + 1))
                }
                disabled={!pagination.hasNext || loading}
                className="rounded-lg border border-slate-300 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          {loading ? (
            <p className="px-2 py-4 text-sm text-slate-500">Laden...</p>
          ) : items.length === 0 ? (
            <p className="px-2 py-4 text-sm text-slate-500">Geen installaties gevonden.</p>
          ) : view === "table" ? (
            <div className="space-y-2">
              <div className="grid gap-2 px-2 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid-cols-[1fr_140px_1.2fr_170px]">
                <span>Installatie</span>
                <span>Type</span>
                <span>Locatie</span>
                <span>Laatst bijgewerkt</span>
              </div>

              {items.map((item) => (
                <Link
                  key={String(item?.id || item?._id || "")}
                  to={`/portaal/installaties/${item?.id || item?._id}`}
                  className="grid gap-2 rounded-xl border border-slate-200 p-3 hover:border-blue-300 hover:bg-blue-50 md:grid-cols-[1fr_140px_1.2fr_170px]"
                >
                  <p className="text-sm font-semibold text-slate-900">{item?.name || item?.naam || "Onbekend"}</p>
                  <p className="text-sm text-slate-700">{item?.typeLabel || formatType(item?.type)}</p>
                  <p className="text-sm text-slate-700">{formatLocation(item)}</p>
                  <p className="text-sm text-slate-700">{formatDate(item?.updatedAt || item?.createdAt)}</p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {items.map((item) => (
                <Link
                  key={String(item?.id || item?._id || "")}
                  to={`/portaal/installaties/${item?.id || item?._id}`}
                  className="rounded-xl border border-slate-200 p-4 hover:border-blue-300 hover:bg-blue-50"
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">{item?.name || item?.naam || "Onbekend"}</p>
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                      {item?.typeLabel || formatType(item?.type)}
                    </span>
                  </div>
                  <div className="space-y-1 text-xs text-slate-600">
                    <p>{formatLocation(item)}</p>
                    <p>Laatst bijgewerkt: {formatDate(item?.updatedAt || item?.createdAt)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function formatType(value) {
  const type = String(value || "").trim().toLowerCase();
  if (!type) return "-";
  if (type === "bmi") return "BMI";
  if (type === "oai") return "OAI";
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function formatLocation(item) {
  return (
    item?.locationLabel || item?.locatie || item?.bagAddress?.formattedAddress || "Locatie onbekend"
  );
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "medium",
  }).format(date);
}

function loadViewMode() {
  try {
    const stored = String(window.localStorage.getItem(VIEW_STORAGE_KEY) || "").trim();
    return stored === "cards" ? "cards" : "table";
  } catch {
    return "table";
  }
}

function saveViewMode(view) {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view === "cards" ? "cards" : "table");
  } catch {
    // ignore localStorage failure
  }
}
